import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Error from '@mui/icons-material/Error';
import { CircularProgress } from '@mui/material';

import Warning from '@mui/icons-material/Warning';
import { MergedAccount, MergeOperation, MergeStatus } from '../../../authentication/Authentication.model';
import { Modal } from '../../../_shared/modal/Modal';
import { RequestError } from '../../../Error.model';
import { myI18n } from '../../../i18n';
import { get, post } from '../../../utils/Request';
import { useSnackbar } from '../../../snackbar/SnackbarContext';
import { useConfig } from '../../../ConfigContext';

import logoSteam from '../../../_assets/steam.svg';
import logoEgs from '../../../_assets/egs.svg';
import logoStadia from '../../../_assets/stadia.svg';
import { ProviderNames } from '../ProviderEnum';

interface Props {
  providerName: string;
  gameId: string;
  disableMerge: boolean;
  disableUnmerge: boolean;
  initialMergedAccount?: MergedAccount;
}

const getStatus = (status: MergeStatus): string => {
  if (!status || status === MergeStatus.UNKNOWN) {
    return MergeStatus.UNMERGED;
  }

  return status;
};

export function ProviderMerge({
  providerName,
  gameId,
  disableMerge,
  disableUnmerge,
  initialMergedAccount,
}: Props): JSX.Element {
  const { t } = useTranslation(['translation', 'error']);
  const snackbar = useSnackbar();
  const config = useConfig();
  const [showCustomErrorModal, setShowCustomErrorModal] = useState(false);
  const [mergedAccount, setMergedAccount] = useState<MergedAccount>();
  const [isIntervalStarted, setIsIntervalStarted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const status = getStatus(mergedAccount?.status);

  const showUnmerge = status === MergeStatus.MERGED
  || (status === MergeStatus.FAILED && mergedAccount.operation === MergeOperation.UNMERGE);
  const disableButton = (showUnmerge && disableUnmerge) || (!showUnmerge && disableMerge);

  const getMergedAccount = async (): Promise<void> => {
    try {
      const mergedAccounts = await get<MergedAccount[]>(
        `${config.clientConfig.host}/players/me/mergedAccounts/${gameId}`,
      );

      const currentMergedAccount = mergedAccounts.find((account) => account.provider === providerName);
      setMergedAccount(currentMergedAccount);
      if (currentMergedAccount?.errorCode) {
        setError(currentMergedAccount.errorCode);
        snackbar.addErrorMessage(currentMergedAccount.errorCode);
      }
    } catch (e) {
      const err = e as RequestError;
      if (err.code) {
        setError(err.code);
        snackbar.addErrorMessage(err.code);
      }
    }
  };

  const closeCustomErrorModal = (): void => {
    setShowCustomErrorModal(false);
  };

  const merge = (): void => {
    setIsLoading(true);
    post(`${config.clientConfig.host}/players/me/merge`, {
      data: {
        providerName,
        gameId,
      },
    }).then(() => {
      setMergedAccount({ ...mergedAccount, status: MergeStatus.IN_PROGRESS });
    }).catch((err: RequestError) => {
      setIsLoading(false);
      setMergedAccount({ ...mergedAccount, status: MergeStatus.FAILED });
      setError(err.code);

      if (err.code === 'OldDataVersionError') {
        setShowCustomErrorModal(true);
      } else {
        snackbar.addErrorMessage(err.code);
      }
    });
  };

  const unmerge = (): void => {
    setIsLoading(true);
    post(`${config.clientConfig.host}/players/me/unmerge`, {
      data: {
        providerName,
        gameId,
      },
    })
      .then(() => {
        setMergedAccount({ ...mergedAccount, status: MergeStatus.IN_PROGRESS });
      })
      .catch((err: RequestError) => {
        setIsLoading(false);
        setMergedAccount({ ...mergedAccount, status: MergeStatus.FAILED });
        setError(err.code);
        snackbar.addErrorMessage(err.code);
      });
  };

  useEffect(() => {
    setMergedAccount(initialMergedAccount);
    setIsLoading(initialMergedAccount?.status === MergeStatus.IN_PROGRESS);
  }, [initialMergedAccount]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    const getPlayerMergedAccountsTimeout = (): void => {
      interval = setInterval(() => {
        getMergedAccount();
      }, 3000);
    };

    if (status === MergeStatus.IN_PROGRESS && !isIntervalStarted) {
      setIsIntervalStarted(true);
      getPlayerMergedAccountsTimeout();
    }
    if (status !== MergeStatus.IN_PROGRESS && isIntervalStarted) {
      clearInterval(interval);
      setIsLoading(false);
      setIsIntervalStarted(false);
    }
    return () => clearInterval(interval);
  }, [mergedAccount?.status]);

  const statusRendering = (): JSX.Element => {
    const showButton = !isLoading
      && !(status === MergeStatus.FAILED && mergedAccount.operation === MergeOperation.UNKNOWN);

    return (
      <>
        <div
          className={`link mx-auto mt-auto ${status === MergeStatus.FAILED ? 'failed-btn' : ''}`}
          data-cy={`${providerName}-merge-status-text`}
        >
          {t(`translation:account.my-account.${status}`)}
          {status === MergeStatus.FAILED ? (
            <Error fontSize='small' className={MergeStatus.FAILED} />
          ) : (
            <div className={`status ${status}`} />
          )}
        </div>
        {showButton && (
          <button
            type='button'
            disabled={isLoading || status === MergeStatus.IN_PROGRESS || disableButton}
            onClick={showUnmerge ? unmerge : merge}
            data-cy={`${providerName}-${showUnmerge ? 'unmerge' : 'merge'}-button`}
            className={`btn btn-block mx-auto mt-3 mb-0
            ${showUnmerge ? 'btn-secondary' : 'btn-primary-game'}`}
          >
            {t(`translation:account.my-account.${showUnmerge ? 'unmerge' : 'merge'}`)}
          </button>
        )}
        {isLoading && (
          <div className='loader p-0 mt-3'><CircularProgress size='50px' sx={{ color: '#CC0000' }} /></div>
        )}
      </>
    );
  };

  const modalErrorRendering = (): JSX.Element => {
    if (error && myI18n.exists(`error:${error}`)) {
      return (
        <p>
          <span>
            {t(
              'translation:account.provider.modal.error.description1',
              {
                error: t(`error:${error}`, {
                  provider: providerName,
                }),
              },
            )}
          </span>
          <br />
          <br />
          <span>
            {t('translation:account.provider.modal.error.description2')}
          </span>
        </p>
      );
    }

    return <p>{t('translation:account.provider.modal.error.descriptionGeneric')}</p>;
  };

  const providerLogo = (): string => {
    switch (providerName) {
      case ProviderNames.STEAM:
        return logoSteam;
      case ProviderNames.EGS:
        return logoEgs;
      case ProviderNames.STADIA:
        return logoStadia;
      default:
        return '';
    }
  };

  return (
    <div className='provider-merge-container text-center'>
      <img src={providerLogo()} alt={providerName} height='70' />
      <h4 className='mb-3'>{t(`translation:${providerName}`)}</h4>

      {statusRendering()}
      {disableButton && (
        <div className='warning-msg'>
          <Warning className='icon warning' />
          {t('translation:account.provider.mergeDisabledWarning')}
        </div>
      )}

      <Modal
        className='unknown-error'
        show={showCustomErrorModal}
        confirm={closeCustomErrorModal}
        textConfirm={t('translation:button.close')}
        classConfirm='btn btn-primary-game'
      >
        <h2 className='text-uppercase'>{t('translation:account.provider.modal.error.title')}</h2>
        {modalErrorRendering()}
      </Modal>
    </div>
  );
}
